import { supabase } from "../supabase";
import { nextWorkTypeSortOrder } from "./estimates";
import {
  applyCatalogVisibilityScope,
  stampOrganizationCatalogScope,
  stampPlatformCatalogScope,
} from "./catalogOwnership";

export const WORK_TYPE_TEMPLATE_SCHEMA_VERSION = 1;

function normalizeTemplateName(templateName) {
  return templateName.trim().toLowerCase();
}

function pickSectionSnapshot(section) {
  return {
    name: section.name,
    record_kind: section.record_kind || "area",
    surface_type: section.surface_type,
    surface_label: section.surface_label || null,
    length_in: section.length_in,
    width_in: section.width_in,
    deduction_type: section.deduction_type || null,
    applies_to_surface_type: section.applies_to_surface_type || null,
    sort_order: section.sort_order ?? 0,
    notes: section.notes || null,
  };
}

function pickMaterialSnapshot(material) {
  return {
    material_id: material.material_id || null,
    quantity: material.quantity ?? null,
    material_name_snapshot: material.material_name_snapshot,
    sku_snapshot: material.sku_snapshot || null,
    unit_of_measure_snapshot: material.unit_of_measure_snapshot || null,
    unit_cost_snapshot: Number(material.unit_cost_snapshot) || 0,
    sell_price_snapshot:
      material.sell_price_snapshot == null || material.sell_price_snapshot === ""
        ? null
        : Number(material.sell_price_snapshot),
    notes: material.notes || null,
    sort_order: material.sort_order ?? 0,
  };
}

function pickLabourSnapshot(labour) {
  return {
    labour_activity: labour.labour_activity,
    pricing_method: labour.pricing_method || "hourly",
    estimated_hours: labour.estimated_hours,
    hourly_rate:
      labour.hourly_rate == null || labour.hourly_rate === ""
        ? null
        : Number(labour.hourly_rate),
    notes: labour.notes || null,
  };
}

export function buildWorkTypeTemplateSnapshot(workType) {
  return {
    schemaVersion: WORK_TYPE_TEMPLATE_SCHEMA_VERSION,
    workType: {
      feature_type_id: workType.feature_type_id || null,
      feature_type_name_snapshot: workType.feature_type_name_snapshot || null,
      name_override: workType.name_override?.trim() || null,
      assembly_template_id: workType.assembly_template_id || null,
      assembly_name_snapshot: workType.assembly_name_snapshot || null,
      assembly_kind_snapshot: workType.assembly_kind_snapshot || null,
      assembly_template_version_snapshot:
        workType.assembly_template_version_snapshot ?? null,
      feature_status: workType.feature_status || "included",
      notes: workType.notes || null,
    },
    sections: (workType.sections || []).map(pickSectionSnapshot),
    materials: (workType.materials || []).map(pickMaterialSnapshot),
    labour: (workType.labour || []).map(pickLabourSnapshot),
  };
}

async function getTemplateAuthContext(organizationId = null) {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  return {
    userId: user?.id || null,
    organizationId: organizationId ?? null,
  };
}

function applyOrganizationFilter(query, organizationId) {
  return applyCatalogVisibilityScope(query, organizationId);
}

export async function loadWorkTypeTemplates(organizationId = null) {
  const { userId } = await getTemplateAuthContext(organizationId);

  if (!userId) return [];

  let query = supabase
    .from("work_type_templates")
    .select("work_type_template_id, template_name, organization_id, created_at")
    .eq("created_by", userId)
    .eq("is_active", true)
    .order("created_at", { ascending: false });

  query = applyOrganizationFilter(query, organizationId);

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

export async function findWorkTypeTemplateByName(templateName, organizationId = null) {
  const templates = await loadWorkTypeTemplates(organizationId);
  const normalized = normalizeTemplateName(templateName);

  return (
    templates.find(
      (template) => normalizeTemplateName(template.template_name) === normalized,
    ) || null
  );
}

export async function saveWorkTypeTemplate(
  templateName,
  workType,
  organizationId = null,
) {
  const trimmed = templateName.trim();
  if (!trimmed) {
    throw new Error("Template name is required.");
  }

  const { userId, organizationId: resolvedOrgId } =
    await getTemplateAuthContext(organizationId);
  const snapshot = buildWorkTypeTemplateSnapshot(workType);

  const payload = resolvedOrgId
    ? stampOrganizationCatalogScope(
        {
          template_name: trimmed,
          created_by: userId,
          snapshot,
          schema_version: WORK_TYPE_TEMPLATE_SCHEMA_VERSION,
        },
        resolvedOrgId,
      )
    : stampPlatformCatalogScope({
        template_name: trimmed,
        created_by: userId,
        snapshot,
        schema_version: WORK_TYPE_TEMPLATE_SCHEMA_VERSION,
      });

  const { data, error } = await supabase
    .from("work_type_templates")
    .insert(payload)
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function replaceWorkTypeTemplate(
  templateId,
  templateName,
  workType,
  organizationId = null,
) {
  const trimmed = templateName.trim();
  if (!trimmed) {
    throw new Error("Template name is required.");
  }

  const { userId } = await getTemplateAuthContext(organizationId);
  const snapshot = buildWorkTypeTemplateSnapshot(workType);

  let query = supabase
    .from("work_type_templates")
    .update({
      template_name: trimmed,
      snapshot,
      schema_version: WORK_TYPE_TEMPLATE_SCHEMA_VERSION,
    })
    .eq("work_type_template_id", templateId)
    .eq("is_active", true);

  if (userId) {
    query = query.eq("created_by", userId);
  }

  query = applyOrganizationFilter(query, organizationId);

  const { data, error } = await query.select("*").single();

  if (error) throw error;
  return data;
}

export async function addEstimateWorkTypeFromTemplate(
  roomId,
  templateId,
  organizationId = null,
) {
  const { userId } = await getTemplateAuthContext(organizationId);

  let templateQuery = supabase
    .from("work_type_templates")
    .select("*")
    .eq("work_type_template_id", templateId)
    .eq("is_active", true);

  if (userId) {
    templateQuery = templateQuery.eq("created_by", userId);
  }

  templateQuery = applyOrganizationFilter(templateQuery, organizationId);

  const { data: template, error: templateError } = await templateQuery.single();

  if (templateError) throw templateError;

  const snapshot = template.snapshot;
  const wt = snapshot?.workType;
  if (!wt) {
    throw new Error("Template snapshot is invalid.");
  }

  const sortOrder = await nextWorkTypeSortOrder(roomId);

  const { data: feature, error: featureError } = await supabase
    .from("estimate_features")
    .insert({
      room_id: roomId,
      feature_type_id: wt.feature_type_id || null,
      feature_type_name_snapshot: wt.feature_type_name_snapshot || null,
      name_override: wt.name_override || null,
      assembly_template_id: wt.assembly_template_id || null,
      assembly_name_snapshot: wt.assembly_name_snapshot || null,
      assembly_kind_snapshot: wt.assembly_kind_snapshot || null,
      assembly_template_version_snapshot:
        wt.assembly_template_version_snapshot ?? null,
      feature_status: wt.feature_status || "included",
      notes: wt.notes || null,
      sort_order: sortOrder,
    })
    .select("*")
    .single();

  if (featureError) throw featureError;

  const estimateFeatureId = feature.estimate_feature_id;

  const sections = snapshot.sections || [];
  if (sections.length) {
    const sectionRows = sections.map((section) => ({
      room_id: roomId,
      estimate_feature_id: estimateFeatureId,
      name: section.name,
      record_kind: section.record_kind || "area",
      surface_type: section.surface_type,
      surface_label: section.surface_label || null,
      length_in: section.length_in,
      width_in: section.width_in,
      deduction_type: section.deduction_type || null,
      applies_to_surface_type: section.applies_to_surface_type || null,
      sort_order: section.sort_order ?? 0,
      notes: section.notes || null,
    }));

    const { error: sectionsError } = await supabase
      .from("estimate_room_sections")
      .insert(sectionRows);

    if (sectionsError) throw sectionsError;
  }

  const materials = snapshot.materials || [];
  if (materials.length) {
    const materialRows = materials.map((material) => ({
      room_id: roomId,
      estimate_feature_id: estimateFeatureId,
      material_id: material.material_id || null,
      quantity: material.quantity ?? null,
      material_name_snapshot: material.material_name_snapshot,
      sku_snapshot: material.sku_snapshot || null,
      unit_of_measure_snapshot: material.unit_of_measure_snapshot || null,
      unit_cost_snapshot: Number(material.unit_cost_snapshot) || 0,
      sell_price_snapshot: material.sell_price_snapshot ?? null,
      notes: material.notes || null,
      sort_order: material.sort_order ?? 0,
    }));

    const { error: materialsError } = await supabase
      .from("estimate_room_materials")
      .insert(materialRows);

    if (materialsError) throw materialsError;
  }

  const labour = snapshot.labour || [];
  if (labour.length) {
    const labourRows = labour.map((row) => ({
      room_id: roomId,
      estimate_feature_id: estimateFeatureId,
      labour_activity: row.labour_activity,
      pricing_method: row.pricing_method || "hourly",
      estimated_hours: row.estimated_hours ?? null,
      hourly_rate: row.hourly_rate ?? null,
      notes: row.notes || null,
    }));

    const { error: labourError } = await supabase
      .from("estimate_room_labour")
      .insert(labourRows);

    if (labourError) throw labourError;
  }

  return feature;
}
